import { useEffect, useState, type FormEvent } from "react";
import { useStartDraftBoq } from "../../api/boq";
import { useWorkOrderSites } from "../../api/quotations";
import { WorkOrderResult } from "../quotations/StartQuotation";

interface Props {
  onStarted: (boqId: number) => void;
  onCancel: () => void;
}

/** Start the draft BOQ: step 1 of a job, priced by hand before any supplier
 * has quoted. The work order number off the job card finds the site, as it
 * does for a quotation captured by hand; the bill starts blank on the City's
 * rate card.
 *
 * It joins the job by that site and work order when the supplier's quotation
 * is captured, and the customer BOQ is raised as a copy of it. */
export default function StartDraftBoq({ onStarted, onCancel }: Props) {
  const start = useStartDraftBoq();
  const [workOrder, setWorkOrder] = useState("");
  const [lookedUp, setLookedUp] = useState("");
  const [siteId, setSiteId] = useState<number | null>(null);
  const found = useWorkOrderSites(lookedUp);
  const sites = found.data?.sites ?? [];

  useEffect(() => {
    const timer = window.setTimeout(() => setLookedUp(workOrder.replace(/\s+/g, "")), 300);
    return () => window.clearTimeout(timer);
  }, [workOrder]);

  useEffect(() => {
    setSiteId(sites.length === 1 ? sites[0].id : null);
  }, [found.data]); // eslint-disable-line react-hooks/exhaustive-deps

  const ready = siteId !== null && lookedUp !== "";

  function onSubmit(event: FormEvent) {
    event.preventDefault();
    if (siteId === null || !ready) {
      return;
    }
    start.mutate({ site_id: siteId, work_order_no: lookedUp }, { onSuccess: (detail) => onStarted(detail.id) });
  }

  return (
    <div className="cm-job">
      <form className="header-form cm-form start-form" aria-label="Start a draft BOQ" onSubmit={onSubmit}>
        <h3>Start a draft BOQ</h3>
        <p className="cm-doc-note">
          Price the job on the City's rate card before the supplier quotes. When their quotation is captured for the
          same work order, this draft is step 1 of that job, and the customer BOQ is raised as a copy of it.
        </p>
        <label>
          Work order number
          <input
            value={workOrder}
            onChange={(e) => setWorkOrder(e.target.value)}
            placeholder="From the job card, e.g. 511029052"
            inputMode="numeric"
            autoFocus
            required
          />
        </label>
        <WorkOrderResult
          number={lookedUp}
          pending={found.isFetching}
          error={found.isError ? found.error.message : null}
          sites={sites}
          siteId={siteId}
          onChoose={setSiteId}
        />
        <div className="move-buttons">
          <button type="submit" className="btn btn-primary" disabled={start.isPending || !ready}>
            Start the draft BOQ
          </button>
          <button type="button" className="btn" onClick={onCancel}>
            Cancel
          </button>
        </div>
        {start.isError ? (
          <p className="notice bad" role="alert">
            {start.error.message}
          </p>
        ) : null}
      </form>
    </div>
  );
}
